import { useCallback, useMemo, useState } from "react";

import { CAPABILITY_CATALOG_V1 } from "@/app/access-presets/data_sources/capabilities-catalog";
import {
  createPermissionProfilePreset,
  duplicatePermissionProfilePreset,
  patchPermissionProfilePreset,
} from "@/app/access-presets/data_sources/services";
import {
  capabilityGroupCount,
  computeForbiddenCapabilities,
  hasVendorMasterConflict,
  masterRequiresAdminRole,
  requiresOwnerRole,
} from "@/app/access-presets/utils/guardrails";
import {
  APIPermissionProfilePreset,
  type APIPermissionProfilePresetValidationErrorType,
  type CompatibleRole,
  type PresetContext,
} from "@/core/data_sources/models/APIPermissionProfilePreset";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  Banner,
} from "@/core/ui/molecules";
import { ErrorResponse } from "@/core/utils/request";

export type PresetEditorMode = "new" | "edit" | "duplicate" | "view";

const ROLES: { code: CompatibleRole; label: string; desc: string }[] = [
  { code: "owner", label: "Owner", desc: "Full tenant administration" },
  { code: "supervisor", label: "Supervisor", desc: "Approval and oversight" },
  { code: "member", label: "Member", desc: "Operational execution" },
  { code: "viewer", label: "Viewer", desc: "Read-only access" },
];

const CONTEXTS: { code: PresetContext; label: string; desc: string }[] = [
  { code: "master", label: "Master", desc: "Cross-workspace admin context" },
  {
    code: "workspace",
    label: "Workspace",
    desc: "Specific operational workspace",
  },
  { code: "vendor", label: "Vendor", desc: "Contractor / external context" },
];

type FormState = {
  name: string;
  preset_code: string;
  description: string;
  compatible_roles: CompatibleRole[];
  contexts: PresetContext[];
  capabilities: string[];
};

const TITLE: Record<PresetEditorMode, string> = {
  new: "New Permission Profile Preset",
  edit: "Edit Permission Profile Preset",
  duplicate: "Duplicate Permission Profile Preset",
  view: "View Permission Profile Preset",
};

const READONLY_TITLE = "View Permission Profile Preset";

function initialForm(
  preset?: APIPermissionProfilePreset,
  mode?: PresetEditorMode,
): FormState {
  const d = preset?.getData();
  if (mode === "duplicate" && d) {
    return {
      name: `${d.name} (Copy)`,
      preset_code: `${d.preset_code}_copy`,
      description: d.description ?? "",
      compatible_roles: [...(d.compatible_roles ?? [])],
      contexts: [...(d.contexts ?? [])],
      capabilities: [...(d.capabilities ?? [])],
    };
  }
  return {
    name: d?.name ?? "",
    preset_code: d?.preset_code ?? "",
    description: d?.description ?? "",
    compatible_roles: [...(d?.compatible_roles ?? [])],
    contexts: [...(d?.contexts ?? [])],
    capabilities: [...(d?.capabilities ?? [])],
  };
}

export function PresetEditorDialog({
  mode,
  onClose,
  onSaved,
  preset,
  readonly: readonlyProp = false,
}: {
  readonly mode: PresetEditorMode;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly preset?: APIPermissionProfilePreset;
  readonly readonly?: boolean;
}) {
  const original = useMemo(() => initialForm(preset, mode), [preset, mode]);
  const [form, setForm] = useState<FormState>(original);
  const [capSearch, setCapSearch] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const codeImmutable = mode === "edit" || mode === "view";
  const readonly = readonlyProp || mode === "view";
  const forbidden = useMemo(
    () => computeForbiddenCapabilities(form.compatible_roles),
    [form.compatible_roles],
  );

  const willVersion = useMemo(() => {
    if (mode !== "edit") return false;
    const sorted = (arr: string[]) => [...arr].sort().join(",");
    return (
      sorted(form.capabilities) !== sorted(original.capabilities) ||
      sorted(form.compatible_roles) !== sorted(original.compatible_roles) ||
      sorted(form.contexts) !== sorted(original.contexts)
    );
  }, [mode, form, original]);

  const vendorMasterConflict = hasVendorMasterConflict(form.contexts);
  const masterNeedsAdmin = masterRequiresAdminRole(
    form.contexts,
    form.compatible_roles,
  );
  const needsOwner = requiresOwnerRole(
    form.capabilities,
    form.compatible_roles,
  );

  const toggleRole = useCallback((role: CompatibleRole) => {
    setForm((prev) => {
      const has = prev.compatible_roles.includes(role);
      const nextRoles = has
        ? prev.compatible_roles.filter((r) => r !== role)
        : [...prev.compatible_roles, role];
      const forbiddenNow = computeForbiddenCapabilities(nextRoles);
      return {
        ...prev,
        compatible_roles: nextRoles,
        capabilities: prev.capabilities.filter(
          (c) => !forbiddenNow.includes(c),
        ),
      };
    });
  }, []);

  const toggleContext = useCallback((ctx: PresetContext) => {
    setForm((prev) => ({
      ...prev,
      contexts: prev.contexts.includes(ctx)
        ? prev.contexts.filter((c) => c !== ctx)
        : [...prev.contexts, ctx],
    }));
  }, []);

  const toggleCapability = useCallback((code: string) => {
    setForm((prev) => ({
      ...prev,
      capabilities: prev.capabilities.includes(code)
        ? prev.capabilities.filter((c) => c !== code)
        : [...prev.capabilities, code],
    }));
  }, []);

  const validate = useCallback(() => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push("Name is required");
    if (!form.preset_code.trim()) errs.push("Preset Code is required");
    else if (!/^[a-z][a-z0-9_]*$/.test(form.preset_code))
      errs.push("Preset Code must be lowercase snake_case (a-z, 0-9, _)");
    if (form.compatible_roles.length === 0)
      errs.push("At least one compatible role is required");
    if (form.contexts.length === 0)
      errs.push("At least one context is required");
    if (form.capabilities.length === 0)
      errs.push("At least one capability is required");
    if (vendorMasterConflict)
      errs.push("Vendor context cannot coexist with Master context");
    if (masterNeedsAdmin)
      errs.push("Master context requires an admin role (owner or supervisor)");
    if (needsOwner)
      errs.push(
        "Capabilities require the owner role in compatible_roles (e.g. library.install)",
      );
    return errs;
  }, [form, vendorMasterConflict, masterNeedsAdmin, needsOwner]);

  const handleSave = useCallback(async () => {
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      if (mode === "duplicate" && preset?.getData().id != null) {
        const resp = await duplicatePermissionProfilePreset(
          preset.getData().id!,
          {
            preset_code: form.preset_code,
            name: form.name,
          },
        ).response;
        if ((resp.data as ErrorResponse).error) throw resp.data;
      } else if (mode === "edit" && preset?.getData().id != null) {
        const resp = await patchPermissionProfilePreset(preset.getData().id!, {
          name: form.name,
          description: form.description,
          compatible_roles: form.compatible_roles,
          contexts: form.contexts,
          capabilities: form.capabilities,
        }).response;
        if ((resp.data as ErrorResponse).error) throw resp.data;
      } else {
        const resp = await createPermissionProfilePreset({
          preset_code: form.preset_code,
          name: form.name,
          description: form.description,
          compatible_roles: form.compatible_roles,
          contexts: form.contexts,
          capabilities: form.capabilities,
        }).response;
        if ((resp.data as ErrorResponse).error) throw resp.data;
      }
      AppStore.instance.actions.notification(
        0,
        `Preset "${form.name}" ${willVersion ? "updated · new v" + ((preset?.getData().version ?? 1) + 1) : "saved"}`,
        2500,
        true,
      );
      onSaved();
      onClose();
    } catch (err) {
      setErrors(parseValidationErrors(err as ErrorResponse));
      setSaving(false);
    }
  }, [mode, preset, form, validate, onSaved, onClose, willVersion]);

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[1100px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">
            {readonly ? READONLY_TITLE : TITLE[mode]}
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            {readonly
              ? "Read-only view of a global Permission Profile Preset."
              : "Define capabilities inside role guardrails. Roles stay system-defined."}
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <Banner>
          <strong>Purpose:</strong>{" "}
          {readonly
            ? "This is a read-only view of a global Permission Profile Preset. Tenant Setup installs tenant-level copies. Roles stay fixed in code; this preset only defines capabilities inside the role guardrails."
            : "Create a global Permission Profile Preset. Tenant Setup installs a tenant-level copy. Roles stay fixed in code; this preset only defines capabilities inside the role guardrails."}
        </Banner>

        <section className="mb-5 mt-4">
          <h3 className="mb-3 text-[15px] font-extrabold text-ink">
            1. Basic Info
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label>
              <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                Preset name <span className="text-red-500">*</span>
              </span>
              <input
                className={adminInputClasses}
                disabled={readonly}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Field Technician"
                value={form.name}
              />
            </label>
            <label>
              <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
                Preset code <span className="text-red-500">*</span>
              </span>
              <input
                className={`${adminInputClasses} font-mono`}
                disabled={codeImmutable || readonly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    preset_code: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "_"),
                  }))
                }
                placeholder="e.g. field_technician"
                value={form.preset_code}
              />
              <span className="mt-1 block text-xs text-ink-muted">
                {codeImmutable
                  ? "Cannot be changed after creation."
                  : "Use lowercase snake_case. Cannot be changed after creation."}
              </span>
            </label>
          </div>
          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Description
            </span>
            <textarea
              className={`${adminInputClasses} min-h-20 w-full py-2.5`}
              disabled={readonly}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Short operational description"
              value={form.description}
            />
          </label>
        </section>

        <section className="mb-5">
          <h3 className="mb-3 text-[15px] font-extrabold text-ink">
            2. Role Compatibility
          </h3>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {ROLES.map((role) => {
              const checked = form.compatible_roles.includes(role.code);
              return (
                <label
                  className={`flex cursor-pointer gap-3 rounded-control border p-3 ${
                    checked ? "border-blue-400 bg-blue-50" : "border-border"
                  }`}
                  key={role.code}
                >
                  <input
                    checked={checked}
                    disabled={readonly}
                    onChange={() => toggleRole(role.code)}
                    type="checkbox"
                  />
                  <div>
                    <div className="text-[13px] font-extrabold text-ink">
                      {role.label}
                    </div>
                    <div className="text-xs text-ink-muted">{role.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
          {form.compatible_roles.length === 0 ? (
            <p className="mt-2 text-xs text-red-600">
              ⚠ At least one role must be selected.
            </p>
          ) : null}
        </section>

        <section className="mb-5">
          <h3 className="mb-3 text-[15px] font-extrabold text-ink">
            3. Context Availability
          </h3>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {CONTEXTS.map((ctx) => {
              const checked = form.contexts.includes(ctx.code);
              return (
                <label
                  className={`flex cursor-pointer gap-3 rounded-control border p-3 ${
                    checked ? "border-blue-400 bg-blue-50" : "border-border"
                  }`}
                  key={ctx.code}
                >
                  <input
                    checked={checked}
                    disabled={readonly}
                    onChange={() => toggleContext(ctx.code)}
                    type="checkbox"
                  />
                  <div>
                    <div className="text-[13px] font-extrabold text-ink">
                      {ctx.label}
                    </div>
                    <div className="text-xs text-ink-muted">{ctx.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
          {vendorMasterConflict ? (
            <p className="mt-2 text-xs text-red-600">
              ⚠ Vendor presets cannot include Master context.
            </p>
          ) : null}
          {masterNeedsAdmin ? (
            <p className="mt-2 text-xs text-red-600">
              ⚠ Master context requires owner or supervisor in compatible roles.
            </p>
          ) : null}
        </section>

        <section className="mb-2">
          <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
            <h3 className="text-[15px] font-extrabold text-ink">
              4. Capabilities
            </h3>
            <span className="text-xs font-extrabold text-indigo-600">
              {form.capabilities.length} selected
            </span>
          </div>
          <input
            className={`${adminInputClasses} mb-3 w-full`}
            disabled={readonly}
            onChange={(e) => setCapSearch(e.target.value)}
            placeholder="Search capabilities..."
            value={capSearch}
          />
          {forbidden.length > 0 && form.compatible_roles.length > 0 ? (
            <div className="mb-3 rounded-control border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <strong>Role guardrails active:</strong> {forbidden.length}{" "}
              {forbidden.length === 1 ? "capability is" : "capabilities are"}{" "}
              blocked by the selected roles.
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            {Object.entries(CAPABILITY_CATALOG_V1).map(([groupName, caps]) => {
              const visible = capSearch
                ? caps.filter(
                    (c) =>
                      c.code.toLowerCase().includes(capSearch.toLowerCase()) ||
                      c.label.toLowerCase().includes(capSearch.toLowerCase()),
                  )
                : caps;
              if (visible.length === 0) return null;
              return (
                <div
                  className="rounded-control border border-border"
                  key={groupName}
                >
                  <div className="flex items-center justify-between border-b border-border bg-panel-muted px-3 py-2 text-[13px] font-extrabold text-ink">
                    <span>{groupName}</span>
                    <span className="text-xs text-ink-muted">
                      {visible.length} items
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-1 p-3 md:grid-cols-2">
                    {visible.map((cap) => {
                      const checked = form.capabilities.includes(cap.code);
                      const isForbidden = forbidden.includes(cap.code);
                      return (
                        <label
                          className={`flex items-start gap-2 ${
                            isForbidden
                              ? "cursor-not-allowed opacity-40"
                              : "cursor-pointer"
                          }`}
                          key={cap.code}
                        >
                          <input
                            checked={checked}
                            disabled={isForbidden || readonly}
                            onChange={() => toggleCapability(cap.code)}
                            type="checkbox"
                          />
                          <div className="min-w-0">
                            <div className="text-[12px] leading-tight text-ink">
                              {cap.label}
                            </div>
                            <div className="font-mono text-[10px] text-ink-muted">
                              {cap.code}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {form.compatible_roles.length > 0 && form.capabilities.length > 0 ? (
          <section className="mt-4">
            <h3 className="mb-2 text-[15px] font-extrabold text-ink">
              Summary
            </h3>
            <AdminPanel className="bg-blue-50 p-[18px]">
              <AdminMiniList>
                <AdminMiniRow>
                  <strong>Assigned to</strong>
                  <span>{form.compatible_roles.join(" / ")}</span>
                </AdminMiniRow>
                <AdminMiniRow>
                  <strong>Available in</strong>
                  <span>{form.contexts.join(" / ") || "—"}</span>
                </AdminMiniRow>
                <AdminMiniRow>
                  <strong>Capabilities</strong>
                  <span>
                    {form.capabilities.length} across{" "}
                    {capabilityGroupCount(form.capabilities)} module
                    {capabilityGroupCount(form.capabilities) > 1 ? "s" : ""}
                  </span>
                </AdminMiniRow>
              </AdminMiniList>
            </AdminPanel>
          </section>
        ) : null}

        {willVersion ? (
          <Banner tone="warning">
            This edit will create v{(preset?.getData().version ?? 1) + 1}.
            Changes to capabilities, compatible_roles or contexts increment the
            version. Already installed tenants are NOT auto-updated.
          </Banner>
        ) : null}

        {errors.length > 0 ? (
          <Banner tone="error">
            <strong>
              {errors.length} validation error{errors.length > 1 ? "s" : ""}:
            </strong>
            <ul className="ml-4 list-disc">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
      </div>

      <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button disabled={saving} onClick={onClose}>
          {readonly ? "Close" : "Cancel"}
        </Button>
        {readonly ? null : (
          <Button disabled={saving} onClick={handleSave} variant="primary">
            Save Preset
            {willVersion ? ` as v${(preset?.getData().version ?? 1) + 1}` : ""}
          </Button>
        )}
      </footer>
    </div>
  );
}

function parseValidationErrors(err: ErrorResponse): string[] {
  const status = (err as ErrorResponse).status;
  const raw = (err as ErrorResponse).error?.message ?? "";
  try {
    const parsed = JSON.parse(
      raw,
    ) as APIPermissionProfilePresetValidationErrorType;
    const messages: string[] = [];
    for (const fieldErrors of Object.values(parsed)) {
      for (const item of fieldErrors ?? []) {
        messages.push(`${item.code}: ${item.message}`);
      }
    }
    if (messages.length > 0) return messages;
  } catch {
    /* not a validation json */
  }
  if (status === 409)
    return ["Conflict: resource already in the requested state."];
  if (status === 400)
    return ["Validation failed. Check the fields and try again."];
  return [raw || "Request failed"];
}
